/**
 * Responsibility: Pure geometry and path math for atlas map rendering.
 * Scope: Owns SVG path generation and coordinate calculations.
 */
import type { AtlasProjectionNode, AtlasProjectionEdge } from './atlas-view-model'

export function nodeRadius(node: { isCurrent: boolean }): number {
  return node.isCurrent ? 18 : 13
}

export function nodeRect(node: { axis: 'horizontal' | 'vertical' | 'radial' }): { x: number; y: number; width: number; height: number; radius: number } {
  if (node.axis === 'vertical') return { x: -14, y: -34, width: 28, height: 68, radius: 12 }
  return { x: -34, y: -14, width: 68, height: 28, radius: 12 }
}

export function edgeAnchor(node: AtlasProjectionNode, target: { screenX: number; screenY: number } | undefined): { x: number; y: number } {
  const dx = Math.sign((target?.screenX ?? node.screenX) - node.screenX)
  const dy = Math.sign((target?.screenY ?? node.screenY) - node.screenY)
  if (node.kind === 'passage') {
    const rect = nodeRect(node)
    if (Math.abs(dx) > Math.abs(dy)) return { x: node.screenX + (rect.width / 2 * dx), y: node.screenY }
    return { x: node.screenX, y: node.screenY + (rect.height / 2 * dy) }
  }
  const radius = nodeRadius(node)
  if (Math.abs(dx) > Math.abs(dy)) return { x: node.screenX + (radius * dx), y: node.screenY }
  return { x: node.screenX, y: node.screenY + (radius * dy) }
}

export function edgePath(edge: AtlasProjectionEdge): string {
  const horizontal = edge.source.gridY === edge.target.gridY
  const source = edgeAnchor(edge.source, edge.target)
  const target = edgeAnchor(edge.target, edge.source)
  const bend = horizontal ? 14 : -14
  const cx = (source.x + target.x) / 2
  const cy = (source.y + target.y) / 2 + bend
  return `M ${source.x} ${source.y} Q ${cx} ${cy} ${target.x} ${target.y}`
}

export function thumbnailClipPath(node: { axis: 'horizontal' | 'vertical' | 'radial' }): string {
  const rect = nodeRect(node)
  const x = rect.x + 1
  const y = rect.y + 1
  const width = rect.width - 2
  const height = rect.height - 2
  const radius = Math.max(8, rect.radius - 1)
  return [
    `M ${x + radius} ${y}`,
    `H ${x + width - radius}`,
    `Q ${x + width} ${y} ${x + width} ${y + radius}`,
    `V ${y + height - radius}`,
    `Q ${x + width} ${y + height} ${x + width - radius} ${y + height}`,
    `H ${x + radius}`,
    `Q ${x} ${y + height} ${x} ${y + height - radius}`,
    `V ${y + radius}`,
    `Q ${x} ${y} ${x + radius} ${y}`,
    'Z',
  ].join(' ')
}

export function thumbnailBounds(node: AtlasProjectionNode): { x: number; y: number; width: number; height: number } {
  if (node.kind === 'passage') {
    const rect = nodeRect(node)
    return { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
  }
  const radius = nodeRadius(node)
  return { x: -radius, y: -radius, width: radius * 2, height: radius * 2 }
}
